/**
 * Admin 인증 컨텍스트
 * GNU Board 관리자 인증을 위한 별도 컨텍스트
 */

import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";
import adminApi, { User } from "../services/adminApi";

interface AdminAuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (mb_id: string, mb_password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(
  undefined
);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error("useAdminAuth must be used within an AdminAuthProvider");
  }
  return context;
};

interface AdminAuthProviderProps {
  children: ReactNode;
}

export const AdminAuthProvider: React.FC<AdminAuthProviderProps> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 인증 상태 확인
  const checkAuth = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("admin_token");

      console.log('🔐 AdminAuthContext: checkAuth started, token exists:', !!token);

      // 토큰이 없으면 바로 종료
      if (!token) {
        console.log('🔐 No token found, setting user to null');
        setUser(null);
        setIsLoading(false);
        return;
      }

      console.log('🔐 Verifying token...');
      const userData = await adminApi.verify();
      console.log('✅ Token verified, user:', userData);
      setUser(userData);
    } catch (error: any) {
      console.error("❌ Admin auth check failed:", error);
      console.error("❌ Error response status:", error.response?.status);
      console.error("❌ Error response data:", error.response?.data);
      console.error("❌ Error message:", error.message);

      // 인증 관련 에러면 토큰 제거
      // - 401 에러
      // - 네트워크 에러 (response가 없는 경우)
      // - 기타 인증 실패
      if (error.response?.status === 401 || !error.response) {
        console.log('🔒 Auth failed (401 or network error), clearing token');
        adminApi.clearToken();
      }

      console.log('🔒 Setting user to null');
      setUser(null);
    } finally {
      console.log('🔐 checkAuth finished, setting isLoading to false');
      setIsLoading(false);
    }
  };

  // 로그인
  const login = async (mb_id: string, mb_password: string) => {
    try {
      console.log('🔑 AdminAuthContext: Calling API login...');
      const response = await adminApi.login(mb_id, mb_password);
      console.log('🔑 AdminAuthContext: API response:', response);
      setUser(response.user);
      console.log('🔑 AdminAuthContext: User state updated:', response.user);
    } catch (error: any) {
      console.error("❌ Admin login failed:", error);
      throw error;
    }
  };

  // 로그아웃
  const logout = async () => {
    try {
      await adminApi.logout();
    } catch (error) {
      console.error("Admin logout error:", error);
    } finally {
      setUser(null);
      adminApi.clearToken();
    }
  };

  // 컴포넌트 마운트시 인증 확인
  useEffect(() => {
    checkAuth();

    // 토큰 제거 이벤트 리스너 (API에서 401 에러 발생 시)
    const handleTokenCleared = () => {
      console.log('🔒 Token cleared event received - logging out');
      setUser(null);
    };

    window.addEventListener('admin_token_cleared', handleTokenCleared);

    return () => {
      window.removeEventListener('admin_token_cleared', handleTokenCleared);
    };
  }, []);

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
